import { TextTypeEnum } from '@lite-workspace/dto-fotoclass-templates';

/** The person fields a vignette prints, narrowed out of the project-user wire shape. */
export interface PersonTextFields {
  firstName?: string;
  lastName?: string;
  fatherName?: string;
  subject?: string;
  description?: string;
}

type PersonTextField = keyof PersonTextFields;

/**
 * Which printed types each field feeds, mirroring `resolveTextByType`: the full name and the
 * given-name-plus-patronymic are composed, so one edited field repaints more than one kind of box.
 */
const FIELD_TEXT_TYPES: Readonly<Record<PersonTextField, readonly TextTypeEnum[]>> = {
  firstName: [TextTypeEnum.PERSON_FIRST_NAME, TextTypeEnum.PERSON_FULL_NAME, TextTypeEnum.PERSON_GIVEN_PATRONYMIC],
  lastName: [TextTypeEnum.PERSON_LAST_NAME, TextTypeEnum.PERSON_FULL_NAME],
  fatherName: [TextTypeEnum.PERSON_PATRONYMIC, TextTypeEnum.PERSON_FULL_NAME, TextTypeEnum.PERSON_GIVEN_PATRONYMIC],
  subject: [TextTypeEnum.PERSON_SUBJECT],
  description: [TextTypeEnum.PERSON_QUOTE],
};

const FIELDS: readonly PersonTextField[] = Object.keys(FIELD_TEXT_TYPES) as PersonTextField[];

// Stable order so a fan-out signature is comparable between two saves of the same shape.
const ORDERED_TEXT_TYPES: readonly TextTypeEnum[] = [
  TextTypeEnum.PERSON_FIRST_NAME,
  TextTypeEnum.PERSON_LAST_NAME,
  TextTypeEnum.PERSON_FULL_NAME,
  TextTypeEnum.PERSON_PATRONYMIC,
  TextTypeEnum.PERSON_GIVEN_PATRONYMIC,
  TextTypeEnum.PERSON_QUOTE,
  TextTypeEnum.PERSON_SUBJECT,
];

// Whitespace-only and absent print the same nothing — `resolveTextByType` trims every field — so one
// turning into the other must not read as an edit.
function readField(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Canonical order, so two collections of the same types compare equal as a plain joined string. */
export function orderPersonTextTypes(textTypes: Iterable<string>): string[] {
  const wanted = new Set<string>(textTypes);
  return ORDERED_TEXT_TYPES.filter(textType => wanted.has(textType));
}

/**
 * Person text types whose printed value differs between two snapshots of a person. Fields outside the
 * printed set (photo, type, phone) report nothing: they are not what a vignette prints.
 */
export function changedPersonTextTypes(before: PersonTextFields | undefined, after: PersonTextFields | undefined): string[] {
  const changed = new Set<string>();

  for (const field of FIELDS) {
    if (readField(before?.[field]) === readField(after?.[field])) {
      continue;
    }

    for (const textType of FIELD_TEXT_TYPES[field]) {
      changed.add(textType);
    }
  }

  return orderPersonTextTypes(changed);
}
